import {
  Button,
  Chip,
  ChipGroup,
  Icon,
  InfoCircleHollowSVG,
  TooltipDialog,
} from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";

import { ContentMaturitiesEnum } from "@/search/NewSearchPageMVP/FilterPanel/FilterControls/FilterControls.utils";
import { convertFilterName } from "@/search/NewSearchPageMVP/FilterPanel/filters/ReadingLevels/GradeLevelChips/GradeLevelChips.utils";
import {
  filterAndTooltipContainerCss,
  subHeadingCss,
  tooltipDialogCss,
  tooltipIconCss,
} from "@/search/NewSearchPageMVP/FilterPanel/filters/ReadingLevels/ReadingLevels.styles";
import { Grades } from "@/search/NewSearchPageMVP/NewSearchPageMVP.constants";
import { useSearchAdminPermissions } from "@/search/NewSearchPageMVP/provider/hooks";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import { UserAttributes } from "@/types/models/UserAttributes.interface";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import useUserAttributes from "@/users/useUserAttributes";
import {
  getGradeLevelChipCss,
  gradeLevelChipGroupCss,
} from "./GradeLevelChips.styles";

export const GradeLevelChips = () => {
  const {
    getters: { validQueryParams },
    handlers: { hasSearchParamValue, toggleSearchParamValue },
  } = useSearchPageContext();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  const { data: user } = useUser();
  // Using a custom tracking instead of withTrackerEvent to avoid accessibility issues and also the withTrackerEvent with Chip component doesn't work properly
  const { trackAction, getEventName } = useTracker();
  const { isStudent } = getUserProperties(user, { shouldUseNewUserApi: false });
  const userAttributesData = useUserAttributes();
  const userAttributes = userAttributesData.userAttributes as UserAttributes;
  const isElementaryStudent = isStudent && userAttributes?.actual_grade < 6;

  const gradeLevelFilterOptions =
    validQueryParams[AgathaSchemaParamsEnum.grade_level]?.items?.values;

  if (!gradeLevelFilterOptions) return null;

  const isLowerElementaryChip = (value: string) =>
    value === Grades.Kindergarten ||
    value === Grades.GRADE_1 ||
    value === Grades.GRADE_2;

  const isUpperElementaryChip = (value: string) =>
    value === Grades.GRADE_3 ||
    value === Grades.GRADE_4 ||
    value === Grades.GRADE_5;

  const isMiddleSchoolChip = (value: string) =>
    value === Grades.GRADE_6 ||
    value === Grades.GRADE_7 ||
    value === Grades.GRADE_8;

  const isHighSchoolChip = (value: string) =>
    value === Grades.GRADE_9 ||
    value === Grades.GRADE_10 ||
    value === Grades.GRADE_11 ||
    value === Grades.GRADE_12;

  const handleFilterClick = (_: unknown, value: string) => {
    trackAction({
      event_name: getEventName(
        objectTypes.content,
        actionPrefixes.initiate,
        actionNames.filter,
      ),
      event_type: eventTypes.click,
      dimension_properties: {
        description: `Filter search by ${value}`,
      },
    });

    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    const isGradeLevelAlreadySelected = hasSearchParamValue(
      AgathaSchemaParamsEnum.grade_level,
      value,
    );

    if (isLowerElementaryChip(value)) {
      const isLowerElementarySelected = hasSearchParamValue(
        AgathaSchemaParamsEnum.content_maturities,
        ContentMaturitiesEnum.lower_elementary,
      );
      if (!isGradeLevelAlreadySelected && !isLowerElementarySelected) {
        toggleSearchParamValue(
          AgathaSchemaParamsEnum.content_maturities,
          ContentMaturitiesEnum.lower_elementary,
        );
      }
    }

    if (isUpperElementaryChip(value)) {
      const isUpperElementarySelected = hasSearchParamValue(
        AgathaSchemaParamsEnum.content_maturities,
        ContentMaturitiesEnum.upper_elementary,
      );
      if (!isGradeLevelAlreadySelected && !isUpperElementarySelected) {
        toggleSearchParamValue(
          AgathaSchemaParamsEnum.content_maturities,
          ContentMaturitiesEnum.upper_elementary,
        );
      }
    }

    if (isMiddleSchoolChip(value)) {
      const isMiddleSchoolSelected = hasSearchParamValue(
        AgathaSchemaParamsEnum.content_maturities,
        ContentMaturitiesEnum.middle_school,
      );
      if (!isGradeLevelAlreadySelected && !isMiddleSchoolSelected) {
        toggleSearchParamValue(
          AgathaSchemaParamsEnum.content_maturities,
          ContentMaturitiesEnum.middle_school,
        );
      }
    }

    if (isHighSchoolChip(value)) {
      const isHighSchoolSelected = hasSearchParamValue(
        AgathaSchemaParamsEnum.content_maturities,
        ContentMaturitiesEnum.high_school,
      );
      if (!isGradeLevelAlreadySelected && !isHighSchoolSelected) {
        toggleSearchParamValue(
          AgathaSchemaParamsEnum.content_maturities,
          ContentMaturitiesEnum.high_school,
        );
      }
    }

    toggleSearchParamValue(AgathaSchemaParamsEnum.grade_level, value);
  };

  return (
    <>
      <div css={filterAndTooltipContainerCss}>
        <h3 css={subHeadingCss}>Grade level</h3>
        <TooltipDialog
          offset={8}
          placement="bottom-start"
          header="Grade level"
          body={
            <>
              This filters the reading level of the content.
              <br />
              <br />
              For example, an article about the summer olympics has 5 levels for
              3rd, 5th, 7th, 9th, and 12th grades.
            </>
          }
          buttonProps={{
            flavor: Button.flavors.incognito,
          }}
          ariaProps={{
            "aria-label": "Grade Lavel Filter Tooltip",
          }}
          __cssFor={tooltipDialogCss}
          buttonContents={
            <Icon
              SvgComponent={InfoCircleHollowSVG}
              __cssFor={tooltipIconCss}
            />
          }
        />
      </div>

      <ChipGroup
        groupName="gradeLevelFilter"
        __cssFor={gradeLevelChipGroupCss}
        onChipEvent={handleFilterClick}
        size={ChipGroup.chipSizes.small}
        chipType={ChipGroup.chipTypes.filter}
      >
        {gradeLevelFilterOptions.map(({ label, value }) => {
          if (isElementaryStudent && isHighSchoolChip(value)) return null;

          const chipLabel = convertFilterName(label);
          const isActive = hasSearchParamValue(
            AgathaSchemaParamsEnum.grade_level,
            value,
          );

          return (
            <Chip
              key={value}
              value={value}
              text={chipLabel}
              active={isActive}
              __cssFor={getGradeLevelChipCss(isActive)}
              dataProps={{
                "data-qa-selector": `select-${label.replace(" ", "")}-search-filter`,
                "data-tracker-model": objectTypes.content,
                "data-testid": `select-${label.replace(" ", "")}-search-filter`,
              }}
            />
          );
        })}
      </ChipGroup>
    </>
  );
};
